import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { TaskEntry } from '../TaskEntry'
import type { TaskEntryProps } from '../TaskEntry'
import { useLazyRow } from '../LazyRows'

interface SortableTaskEntryProps extends TaskEntryProps {
  /** Task ID used as the sortable identifier */
  sortableId: string
  /** Whether this item is part of a dragged group but not the active drag item */
  isDraggedGroup?: boolean
  /** Number of items being dragged (shown as badge on active item) */
  dragCount?: number
  /** True while the DragOverlay is showing this item (keeps original hidden) */
  isOverlayActive?: boolean
  /** True when the active drag item is over a different segment */
  isCrossSegmentDrag?: boolean
  /** True while any drag is in progress — enables sortable transitions */
  isDragActive?: boolean
  /**
   * **Einhänge-Ziel** beim Ziehen (#204): `armed` trägt den Aufnahme-Umriss und „Einhängen“;
   * `blocked` sagt gedämpft, dass eine wiederkehrende Aufgabe nicht eingehängt werden kann.
   */
  nestState?: 'armed' | 'blocked'
}

const SortableRow: React.FC<SortableTaskEntryProps> = ({
  sortableId,
  isDraggedGroup,
  dragCount: _dragCount,
  isOverlayActive,
  isCrossSegmentDrag,
  isDragActive,
  nestState,
  ...taskEntryProps
}) => {
  const { t } = useTranslation()
  // Disable sorting when the item is being edited/created.
  // This prevents Space key in text inputs from triggering a drag.
  const isDisabled = taskEntryProps.mode === 'expanded' || taskEntryProps.mode === 'creating'

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: isDisabled,
    // Only enable transitions while a drag is active — smooth reorder during drag.
    // After the drop, transitions are off so any transform changes (from useLiveQuery
    // catching up, optimistic update cleanup, or dnd-kit reset) happen instantly
    // instead of causing a visible "double positioning" animation.
    transition: isDragActive ? { duration: 300, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' } : null,
    // Disable FLIP animations on layout changes. After a drop, clearing
    // optimisticUpdates can briefly revert items to old order before useLiveQuery
    // catches up. The default animateLayoutChanges would compute stale transforms
    // during that window, causing visible displacement.
    animateLayoutChanges: () => false,
  })

  // Expanded/creating: clear all dnd-kit visual effects so stale transforms
  // from a recent drag don't interfere with the expanded card layout.
  // Collapsed: standard dnd-kit styling (hidden active item, group opacity, etc.)
  // No opacity transition needed — DragOverlay covers the source item seamlessly
  // on drag start, and all items appear simultaneously on drop (via batched state clear).
  const style = useMemo<React.CSSProperties>(
    () =>
      isDisabled
        ? { position: 'relative' }
        : {
            transform: CSS.Transform.toString(transform),
            transition: transition ?? undefined,
            opacity: (isDragging || isOverlayActive) ? 0 : undefined,
            zIndex: isDragging ? 10 : undefined,
            position: 'relative',
          },
    [isDisabled, transform, transition, isDragging, isOverlayActive]
  )

  // Collapse group items (not the active drag item) with CSS grid trick.
  // Also collapse the active item when dragging cross-segment (close the gap
  // in the source segment — the target segment opens its own gap via strategy).
  const isCollapsing =
    (isDraggedGroup && !isDragging && !isOverlayActive) ||
    (isOverlayActive && isCrossSegmentDrag)
  // Zugeklappt nach dem Loslassen: Die Zeile ist eingehängt und verlässt die Liste.
  const isLeaving = isCollapsing && !isDragActive

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-task-id={sortableId}
      className={[
        'sortable-task-entry',
        isCollapsing ? 'sortable-task-entry--collapsing' : '',
        isLeaving ? 'sortable-task-entry--leaving' : '',
        nestState ? `sortable-task-entry--nest-${nestState}` : '',
      ].filter(Boolean).join(' ')}
      {...attributes}
      /*
        Aufgeklappt ist das Ziehen aus, und dnd-kit meldet dann `aria-disabled` — am Kasten, der die ganze offene Karte
        trägt. Screenreader lasen damit jeden Button darin als deaktiviert vor, und Playwright klickte keinen davon
        (gemessen 10.10.2026: Belohnung in Demnächst). Die Karte ist bedienbar; nur das Ziehen ruht.
      */
      aria-disabled={isDisabled ? undefined : attributes['aria-disabled']}
      {...listeners}
    >
      <TaskEntry {...taskEntryProps} />
      {nestState && (
        <span className="sortable-task-entry__nest-hint" aria-hidden="true">
          {t(nestState === 'armed' ? 'taskList.nest.target' : 'taskList.nest.blocked')}
        </span>
      )}
    </div>
  )
}

/*
  **Lazy Rendering** (#303): Die äußere Hülle trägt nur den Observer und entscheidet; gebaut wird `SortableRow` mit
  `useSortable` und `TaskEntry` erst nahe am Bild. Weit draußen steht ein Platzhalter mit gemerkter Höhe und
  `data-task-id` — Tastatur, Sprung zu einer Aufgabe und Auswahl-Fläche finden ihn, Drag-Ziel ist er nicht (Sven,
  10.10.2026: Ziele nur im gebauten Bereich; scrollt die Liste beim Drag, werden sie gebaut). So läuft bei ⌘A, das
  `selected` an jeder Zeile ändert, `useSortable` nur in den gebauten Zeilen (gemessen 10.10.2026: 150 Hüllen
  renderten mit, ⌘A 103 ms).

  Gebaut bleibt, was einen Zustand trägt (fokussiert, aufgeklappt, gezogen, Nest-Ziel, offenes Kürzel-Menü) —
  „ausgewählt“ nicht: ⌘A baute sonst die ganze Liste.
*/
const SortableTaskEntryComponent: React.FC<SortableTaskEntryProps> = (props) => {
  const { sortableId, isOverlayActive, nestState } = props
  const rowRef = useRef<HTMLDivElement | null>(null)
  const isDisabled = props.mode === 'expanded' || props.mode === 'creating'
  const pinned = !!props.focused || isDisabled || !!isOverlayActive || !!nestState
    || !!props.initialExpanded || props.menuShortcut?.taskId === sortableId
  const { built, placeholderHeight } = useLazyRow(sortableId, rowRef, pinned)
  return (
    <div ref={rowRef} className="lazy-task-row">
      {built
        ? <SortableRow {...props} />
        // Die Höhe gibt es nur hier, je Zeile gemessen — als CSS-Variable, wie die Lage der Einfügelinie.
        : <div className="sortable-task-entry sortable-task-entry--placeholder" data-task-id={sortableId} style={{ '--lazy-row-height': `${placeholderHeight}px` } as React.CSSProperties} />}
    </div>
  )
}

// Memoized so a re-render of TaskList (e.g. editingTaskId flipping on one row)
// only re-renders the rows whose props actually changed. Requires TaskList to
// pass reference-stable handlers — otherwise inline arrows defeat the memo.
export const SortableTaskEntry = React.memo(SortableTaskEntryComponent)
